import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import UserPerformanceGraph from "./UserPerformanceGraph";
import { getUserPerformance } from "../../services/dashboard-api";
import "./styles/dashboard.css";

const UserPerformance = () => {
  const [dateRange, setDateRange] = useState("This Month");
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const dropdownRef = useRef(null);

  // Custom date picker states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  const ranges = ["Today", "Yesterday", "Last 7 Days", "This Month", "Custom Date"];

  const filterMap = {
    Today: "today",
    Yesterday: "yesterday",
    "Last 7 Days": "last_7_days",
    "This Month": "this_month",
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsRangeOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Helper to format date as YYYY-MM-DD in local timezone
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        let response;
        if (dateRange === 'Custom Date' && selectedStartDate && selectedEndDate) {
          // Use custom date range
          const params = {
            start_date: formatLocalDate(selectedStartDate),
            end_date: formatLocalDate(selectedEndDate),
          };
          response = await getUserPerformance(null, params);
        } else {
          // Use predefined filter
          response = await getUserPerformance(filterMap[dateRange]);
        }

        if (response.status === "success") {
          setPerformanceData(response.data);
        }
      } catch (error) {
        console.error("Error fetching user performance:", error);
      }
    };
    fetchData();
  }, [dateRange, selectedStartDate, selectedEndDate]);

  // Date picker helper functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date > today;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!selectedStartDate && !selectedEndDate) return false;
    return (
      (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
      (selectedEndDate && date.getTime() === selectedEndDate.getTime())
    );
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else if (selectedStartDate && !selectedEndDate) {
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
      } else {
        setSelectedEndDate(date);
      }
      // Close date picker and update date range after selecting end date
      setTimeout(() => {
        setShowDatePicker(false);
        setDateRange("Custom Date");
      }, 300);
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateRange = () => {
    if (selectedStartDate && selectedEndDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      const endStr = selectedEndDate.toLocaleDateString("en-GB");
      return `${startStr} - ${endStr}`;
    } else if (selectedStartDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      return `${startStr} - `;
    }
    return dateRange;
  };

  return (
    <div
      className="dashboard-card"
      style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <h3 className="dashboard-card-title" style={{ marginBottom: "4px" }}>
            User Performance Overview
          </h3>
          <p className="dashboard-subtitle">
            Top contributors by sales & operations performance
          </p>
        </div>

        {/* Date Filter Dropdown */}
        <div ref={dropdownRef} style={{ position: "relative" }}>
          <button
            className="dashboard-btn-filter"
            onClick={() => setIsRangeOpen((o) => !o)}
            style={{
              minWidth: "150px",
              justifyContent: "space-between",
              padding: "8px 12px",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {dateRange === "Custom Date" && selectedStartDate && selectedEndDate
                ? formatDateRange()
                : dateRange}
            </span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {isRangeOpen && (
            <div
              style={{
                position: "absolute",
                top: "48px",
                right: 0,
                background: "#fff",
                border: "1px solid #E5E7EB",
                boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                padding: "10px",
                minWidth: "9.5rem",
                zIndex: 10,
              }}
            >
              {ranges.map((range) => (
                <button
                  key={range}
                  onClick={() => {
                    if (range === "Custom Date") {
                      setIsRangeOpen(false);
                      setShowDatePicker(true);
                    } else {
                      setDateRange(range);
                      setSelectedStartDate(null);
                      setSelectedEndDate(null);
                      setIsRangeOpen(false);
                    }
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    border: "none",
                    background: dateRange === range ? "#F3F4F6" : "transparent",
                    borderRadius: "8px",
                    color: "#111827",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <span>{range}</span>
                  {dateRange === range && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#2563EB"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Custom Date Picker Popup */}
          {showDatePicker && (
            <>
              <div
                className="dashboard-date-picker-backdrop"
                onClick={() => setShowDatePicker(false)}
              ></div>
              <div className="dashboard-date-picker" ref={datePickerRef}>
                <div className="dashboard-date-picker-header">
                  <button
                    className="dashboard-date-picker-nav-btn"
                    onClick={() => navigateMonth(-1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(90deg)" }}
                    />
                  </button>
                  <h3 className="dashboard-date-picker-month-year">
                    {currentMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h3>
                  <button
                    className="dashboard-date-picker-nav-btn"
                    onClick={() => navigateMonth(1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(-90deg)" }}
                    />
                  </button>
                </div>

                <div className="dashboard-date-picker-weekdays">
                  <div className="dashboard-weekday">Mo</div>
                  <div className="dashboard-weekday">Tu</div>
                  <div className="dashboard-weekday">We</div>
                  <div className="dashboard-weekday">Th</div>
                  <div className="dashboard-weekday">Fr</div>
                  <div className="dashboard-weekday">Sa</div>
                  <div className="dashboard-weekday">Su</div>
                </div>

                <div className="dashboard-date-picker-days">
                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <div
                      key={index}
                      className={`dashboard-date-picker-day ${
                        day ? "" : "empty"
                      } ${day && isDateDisabled(day) ? "disabled" : ""} ${
                        day && isDateInRange(day) ? "in-range" : ""
                      } ${day && isDateSelected(day) ? "selected" : ""}`}
                      onClick={() => day && handleDateClick(day)}
                    >
                      {day ? day.getDate() : ""}
                    </div>
                  ))}
                </div>

                {selectedStartDate && selectedEndDate && (
                  <div className="dashboard-date-picker-footer">
                    <span className="dashboard-date-picker-selected">
                      {formatDateRange()}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#93C5FD",
              borderRadius: "3px",
            }}
          ></div>
          <span
            style={{ fontSize: "13px", color: "#4B5563", fontWeight: "500" }}
          >
            Sales Managed
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#6EE7B7",
              borderRadius: "3px",
            }}
          ></div>
          <span
            style={{ fontSize: "13px", color: "#4B5563", fontWeight: "500" }}
          >
            Profit Generated
          </span>
        </div>
      </div>

      {performanceData && <UserPerformanceGraph data={performanceData} />}
    </div>
  );
};

export default UserPerformance;
